/**
 * Dashboard progress and focus-area summary for the learner diagnostic profile.
 *
 * @module DiagnosticProgressWidget
 */

import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Stethoscope,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useDiagnosticProfile } from "@/hooks/useDiagnosticProfile";
import {
  buildDiagnosticModuleProgress,
  DIAGNOSTIC_MODULE_ORDER,
} from "@/lib/diagnosticProgress";
import { ROUTES } from "@/lib/routeConstants";
import { cn } from "@/lib/utils";

function LoadingState() {
  return (
    <Card className="border-border" aria-busy="true">
      <CardContent className="space-y-3 p-4 sm:p-6">
        <div className="h-5 w-48 animate-pulse rounded bg-muted" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {DIAGNOSTIC_MODULE_ORDER.map((module) => (
            <div
              key={module}
              className="h-20 animate-pulse rounded-lg bg-muted/70"
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ErrorState() {
  const { t } = useTranslation();
  return (
    <Card className="border-border">
      <CardContent className="flex items-center gap-3 p-4 sm:p-6">
        <AlertCircle
          className="h-5 w-5 shrink-0 text-destructive"
          aria-hidden
        />
        <p className="font-ui text-sm text-muted-foreground">
          {t("diagnostic.dashboard.error", {
            defaultValue: "Diagnostic progress could not be loaded.",
          })}
        </p>
      </CardContent>
    </Card>
  );
}

export function DiagnosticProgressWidget() {
  const { t } = useTranslation();
  const { profile, isLoading, isError } = useDiagnosticProfile();

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState />;
  if (!profile?.enabled) return null;

  const modules = buildDiagnosticModuleProgress(profile);

  return (
    <Card className="border-border">
      <CardHeader className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between sm:p-6">
        <div className="flex min-w-0 gap-3">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Stethoscope className="h-4 w-4" aria-hidden />
          </span>
          <div>
            <CardTitle className="font-ui text-base">
              {t("diagnostic.dashboard.title", {
                defaultValue: "Diagnostic profile progress",
              })}
            </CardTitle>
            <CardDescription className="font-ui mt-1">
              {t("diagnostic.dashboard.subtitle", {
                defaultValue:
                  "Answer distinct questions to unlock detailed focus areas in each skill.",
              })}
            </CardDescription>
          </div>
        </div>
        <Button variant="ghost" size="sm" asChild className="self-start">
          <Link to={ROUTES.DIAGNOSTIC_PROFILE}>
            <span className="font-ui">
              {t("diagnostic.dashboard.viewProfile", {
                defaultValue: "View profile",
              })}
            </span>
            <ArrowRight className="ml-1 h-3.5 w-3.5" aria-hidden />
          </Link>
        </Button>
      </CardHeader>
      <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {modules.map((item) => (
            <div
              key={item.module}
              className={cn(
                "rounded-lg border p-3",
                item.unlocked
                  ? "border-primary/30 bg-primary/5"
                  : "border-border bg-background/70",
              )}
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="font-ui truncate text-xs font-medium capitalize text-foreground">
                  {t(`moduleSelection.modules.${item.module}.label`, {
                    defaultValue: item.module,
                  })}
                </span>
                <span
                  className={cn(
                    "font-ui shrink-0 text-xs font-semibold",
                    item.unlocked ? "text-primary" : "text-muted-foreground",
                  )}
                >
                  {item.attempted} / {item.required}
                </span>
              </div>
              <Progress
                value={item.percentage}
                className="h-1.5 bg-muted"
                aria-label={t("diagnostic.dashboard.progressLabel", {
                  defaultValue:
                    "{{module}} diagnostic progress: {{attempted}} of {{required}}",
                  module: item.module,
                  attempted: item.attempted,
                  required: item.required,
                })}
              />
              <div className="mt-2">
                {item.unlocked ? (
                  <p className="font-ui flex items-center gap-1 text-[11px] font-medium text-primary">
                    <CheckCircle2 className="h-3 w-3 shrink-0" aria-hidden />
                    {t("diagnostic.dashboard.unlocked", {
                      defaultValue: "Detailed profile unlocked",
                    })}
                  </p>
                ) : (
                  <p className="font-ui text-[11px] text-muted-foreground">
                    {t("diagnostic.dashboard.remaining", {
                      defaultValue: "{{count}} more unique question",
                      defaultValue_other: "{{count}} more unique questions",
                      count: item.remaining,
                    })}
                  </p>
                )}
                {item.unlocked && (
                  <p className="font-ui mt-1.5 line-clamp-2 text-xs text-foreground">
                    {item.focusArea
                      ? item.focusArea.explain
                      : t("diagnostic.profile.noWeaknesses", {
                          defaultValue: "No standout weaknesses — nice work!",
                        })}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
